import { useEffect, useState } from "react";

import {
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Textarea,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { privilegeSchema } from "@/schemas/privilegeSchema";
import {
    getAllPrivileges,
    getAllRefDataPrivileges,
    getPrivilegeById,
    postPrivilege,
    putPrivilege,
} from "@/services/privilegeManagementService";
import { getAllMenus } from "@/services/menuManagementService";

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

type PrivilegeFormValues = {
    name: string;
    description: string;
    isParent: boolean;
    parentId: string;
    menuId: string;
    create_permission: string;
    view_list_permission: string;
    view_detail_permission: string;
    update_permission: string;
    delete_permission: string;
};

const getErrorMessage = (error: unknown): string | undefined =>
    typeof error === "string" ? error : undefined;

const PermissionSelect = ({ label, refDataOptions, value, onChange }: {
    label: string;
    fieldName: string;
    refDataOptions: any[];
    value: string;
    onChange: (val: string) => void;
}) => (
    <div style={{ marginBottom: '12px' }}>
        <FormLabel fontSize="sm">{label}</FormLabel>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
        >
            <option value="">-- None --</option>
            {refDataOptions.map((r: any) => (
                <option key={r.id} value={r.id}>{r.methodName}</option>
            ))}
        </select>
    </div>
);

const EditPrivilegeManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isOpen: isOpenConfirm,
        onOpen: onOpenConfirm,
        onClose: onCloseConfirm,
    } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [data, setData] = useState<any>(null);
    const [refDataOptions, setRefDataOptions] = useState<any[]>([]);
    const [menuOptions, setMenuOptions] = useState<any[]>([]);
    const [parentPrivilegeOptions, setParentPrivilegeOptions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'save' | 'cancel' | null>(null);
    const [pendingValues, setPendingValues] = useState<PrivilegeFormValues | null>(null);

    const { id } = location.state || {};
    const isEdit = Boolean(id);

    const fetchDropdowns = async () => {
        try {
            const [refDataResp, menusResp, privilegesResp] = await Promise.all([
                getAllRefDataPrivileges() as any,
                getAllMenus({ search: '', pageNumber: 1, pageSize: 1000 }) as any,
                getAllPrivileges({ search: '', pageNumber: 1, pageSize: 1000 }) as any,
            ]);
            if (refDataResp.status === HttpStatusCode.Ok) setRefDataOptions(refDataResp.data || []);
            if (menusResp.status === HttpStatusCode.Ok) setMenuOptions(menusResp.data || []);
            if (privilegesResp.status === HttpStatusCode.Ok) {
                const parents = (privilegesResp.data || []).filter((p: any) => p.isParent);
                setParentPrivilegeOptions(parents);
            }
        } catch {
            // non-critical
        }
    };

    const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await getPrivilegeById(id) as any;
            if (response.status === HttpStatusCode.Ok && response.data) {
                setData(response.data);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load privilege.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                navigate('/settings/privilege-management');
            }
        } catch {
            toast({
                title: 'Load error',
                description: "An error occurred while loading the privilege.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            navigate('/settings/privilege-management');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdowns();
        if (isEdit) fetchData();
    }, [id]);

    const handleSubmit = async (values: PrivilegeFormValues) => {
        setPendingValues(values);
        setConfirmAction('save');
        onOpenConfirm();
    };

    const handleConfirmSave = async (values: PrivilegeFormValues) => {
        try {
            const obj = {
                ...values,
                parentId: values.isParent ? EMPTY_GUID : (values.parentId || EMPTY_GUID),
                menuId: values.menuId || null,
                create_permission: values.create_permission || null,
                view_list_permission: values.view_list_permission || null,
                view_detail_permission: values.view_detail_permission || null,
                update_permission: values.update_permission || null,
                delete_permission: values.delete_permission || null,
                ...(isEdit && { id: data.id }),
                modifiedBy: name || "System",
                createdBy: name || "System",
            };

            const response = isEdit
                ? await putPrivilege(obj) as any
                : await postPrivilege(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'Success',
                    description: isEdit ? "Privilege updated successfully." : "Privilege created successfully.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                setTimeout(() => navigate('/settings/privilege-management'), 700);
            } else {
                toast({
                    title: 'Save failed',
                    description: response?.message || "Failed to save privilege.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: 'Save error',
                description: "An error occurred while saving the privilege.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const handleCancel = () => {
        setConfirmAction('cancel');
        onOpenConfirm();
    };

    const handleConfirmCancel = () => {
        navigate('/settings/privilege-management');
    };

    const initialValues: PrivilegeFormValues = data
        ? {
            name: data.name || '',
            description: data.description || '',
            isParent: data.isParent ?? false,
            parentId: data.parentId || EMPTY_GUID,
            menuId: data.menuId || '',
            create_permission: data.create_permission || '',
            view_list_permission: data.view_list_permission || '',
            view_detail_permission: data.view_detail_permission || '',
            update_permission: data.update_permission || '',
            delete_permission: data.delete_permission || '',
        }
        : {
            name: '',
            description: '',
            isParent: false,
            parentId: EMPTY_GUID,
            menuId: '',
            create_permission: '',
            view_list_permission: '',
            view_detail_permission: '',
            update_permission: '',
            delete_permission: '',
        };

    return (
        <div style={{ padding: '20px 15px', maxWidth: '700px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', cursor: 'pointer' }} onClick={() => navigate('/settings/privilege-management')}>
                <ArrowIcon style={{ transform: 'rotate(180deg)' }} />
                <span>Back</span>
            </div>

            <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                {isEdit ? 'Edit Privilege' : 'Add Privilege'}
            </h2>

            {isLoading && !data && <div>Loading...</div>}

            {!isLoading && (
                <Formik<PrivilegeFormValues>
                    initialValues={initialValues}
                    validationSchema={privilegeSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <div style={{ marginBottom: '15px' }}>
                                <Field name="name">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.name && touched.name)}>
                                            <FormLabel>Name *</FormLabel>
                                            <Input {...field} placeholder="e.g., Manage Invoices" />
                                            <FormErrorMessage>{getErrorMessage(errors.name)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="description">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.description && touched.description)}>
                                            <FormLabel>Description</FormLabel>
                                            <Textarea {...field} placeholder="Enter description (optional)" />
                                            <FormErrorMessage>{getErrorMessage(errors.description)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px', display: 'flex', gap: '24px' }}>
                                <Field name="isParent">
                                    {({ field }: FieldProps) => (
                                        <Checkbox
                                            isChecked={field.value}
                                            onChange={(e) => {
                                                setFieldValue('isParent', e.target.checked);
                                                if (e.target.checked) setFieldValue('parentId', EMPTY_GUID);
                                            }}
                                        >
                                            Is Parent
                                        </Checkbox>
                                    )}
                                </Field>
                            </div>

                            {!values.isParent && (
                                <div style={{ marginBottom: '15px' }}>
                                    <FormLabel>Parent Privilege</FormLabel>
                                    <select
                                        value={values.parentId}
                                        onChange={(e) => setFieldValue('parentId', e.target.value)}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                    >
                                        <option value={EMPTY_GUID}>-- No Parent --</option>
                                        {parentPrivilegeOptions.map((p: any) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{ marginBottom: '15px' }}>
                                <FormLabel>Linked Menu</FormLabel>
                                <select
                                    value={values.menuId}
                                    onChange={(e) => setFieldValue('menuId', e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                >
                                    <option value="">-- None --</option>
                                    {menuOptions.map((m: any) => (
                                        <option key={m.id} value={m.id}>{m.name} ({m.url})</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                                <FormLabel fontWeight="semibold" marginBottom="12px">Permission Mappings (RefData)</FormLabel>
                                <PermissionSelect label="Create Permission" fieldName="create_permission" refDataOptions={refDataOptions} value={values.create_permission} onChange={(v) => setFieldValue('create_permission', v)} />
                                <PermissionSelect label="View List Permission" fieldName="view_list_permission" refDataOptions={refDataOptions} value={values.view_list_permission} onChange={(v) => setFieldValue('view_list_permission', v)} />
                                <PermissionSelect label="View Detail Permission" fieldName="view_detail_permission" refDataOptions={refDataOptions} value={values.view_detail_permission} onChange={(v) => setFieldValue('view_detail_permission', v)} />
                                <PermissionSelect label="Update Permission" fieldName="update_permission" refDataOptions={refDataOptions} value={values.update_permission} onChange={(v) => setFieldValue('update_permission', v)} />
                                <PermissionSelect label="Delete Permission" fieldName="delete_permission" refDataOptions={refDataOptions} value={values.delete_permission} onChange={(v) => setFieldValue('delete_permission', v)} />
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button type="submit" colorScheme='blue' isLoading={isSubmitting}>
                                    Save
                                </Button>
                                <Button onClick={handleCancel} variant='outline'>
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            <ModalConfirmation
                isOpen={isOpenConfirm}
                onClose={onCloseConfirm}
                title={confirmAction === 'save' ? 'Confirm Save' : 'Confirm Cancel'}
                confirmationText={confirmAction === 'save'
                    ? 'Are you sure you want to save this privilege?'
                    : 'Are you sure you want to cancel? Unsaved changes will be lost.'}
                handleCancel={onCloseConfirm}
                handleConfirm={() => {
                    if (confirmAction === 'save' && pendingValues) {
                        handleConfirmSave(pendingValues);
                    } else {
                        handleConfirmCancel();
                    }
                    onCloseConfirm();
                }}
                confirmButtonText={confirmAction === 'save' ? 'Save' : 'Yes'}
                cancelButtonText={confirmAction === 'save' ? 'Cancel' : 'No'}
            />
        </div>
    );
};

export default EditPrivilegeManagement;
